import { addDays } from './dates';
import { fitnessSeries, type FitnessDay } from './fitness';
import type { PlanType } from './vocab';

export interface PlanTypeSpec { intensity: number; defaultS: number }

// Rest has no Intensity or duration. Every other type is ridden at a fixed Intensity, for its default when none is set.
export const PLAN_TABLE: Record<PlanType, PlanTypeSpec> = {
  rest: { intensity: 0, defaultS: 0 },
  recovery: { intensity: 0.55, defaultS: 3600 },
  endurance: { intensity: 0.68, defaultS: 7200 },
  tempo: { intensity: 0.8, defaultS: 5400 },
  'sweet-spot': { intensity: 0.88, defaultS: 5400 },
  threshold: { intensity: 0.95, defaultS: 4500 },
  'vo2-max': { intensity: 0.9, defaultS: 4500 },
  race: { intensity: 0.9, defaultS: 7200 },
};

export const PLAN_DURATION_S = { min: 5 * 60, max: 12 * 3600 } as const;
export const PLAN_NOTE_MAX = 140;
export const FORECAST_DAYS = 42;

export const loadPerHour = (type: PlanType): number => PLAN_TABLE[type].intensity ** 2 * 100;

export interface PlanEstimate { load: number; durationS: number; defaulted: boolean }

export function estimatePlan(type: PlanType, durationS: number | null): PlanEstimate {
  if (type === 'rest') return { load: 0, durationS: 0, defaulted: false };
  const seconds = durationS ?? PLAN_TABLE[type].defaultS;
  return { load: Math.round((loadPerHour(type) * seconds) / 3600), durationS: seconds, defaulted: durationS === null };
}

export interface PlannedLoad { date: string; load: number }

// From today, or tomorrow once today has a counted ride (the seed already holds its Load), to the last planned day after
// today, at most FORECAST_DAYS ahead. Unplanned days count as 0 Load. With no plan after today there is no forecast.
export function planForecast(input: {
  today: string; todayRidden: boolean; seed: { fitness: number; fatigue: number }; plans: readonly PlannedLoad[];
}): FitnessDay[] {
  const cap = addDays(input.today, FORECAST_DAYS);
  const ahead = input.plans.filter((p) => p.date > input.today && p.date <= cap).map((p) => p.date);
  if (ahead.length === 0) return [];
  const from = input.todayRidden ? addDays(input.today, 1) : input.today;
  const to = ahead.reduce((last, date) => (date > last ? date : last));
  const dailyLoads = new Map(input.plans.filter((p) => p.date >= from && p.date <= to).map((p) => [p.date, p.load]));
  return fitnessSeries({ dailyLoads, from, to, seed: input.seed });
}
